import { z } from 'zod'
import type { SimulationPublic } from './simulation'

/**
 * 웹 공유 링크의 공개 범위 (C54, 2026-10-27). 앱에서는 '공개 모드 / 비공개 모드'로 부른다 (2026-10-30 대표 결정, P0-03).
 * full = 공개 모드, 배합·원가까지 전체. no_cost_formula = 비공개 모드, 원가·배합을 응답에서 아예 뺀다 (HTML 숨김이 아니라 데이터 제거).
 */
export const SHARE_SCOPES = ['full', 'no_cost_formula'] as const
export type ShareScope = (typeof SHARE_SCOPES)[number]
/**
 * 새로 만드는 링크의 기본값 = 비공개 모드 (명세 P0-03 '게시해도 원본 배합과 원가가 자동 공개되지 않는다').
 * 범위를 보내지 않는 옛 앱(1.0.3~1.0.7)이 새로 만드는 링크도 이 값이다.
 */
export const NEW_SHARE_SCOPE: ShareScope = 'no_cost_formula'
/**
 * 범위 칸이 생기기 전(C54 이전)에 만든 링크의 값 = 공개 모드. DB 칸의 기본값이라 바꾸지 않는다 —
 * 이미 만든 링크는 지금처럼 전체 공개로 남는다. 새 링크는 서버가 늘 범위를 적어 넣으므로 이 기본값을 쓰지 않는다
 */
export const LEGACY_SHARE_SCOPE: ShareScope = 'full'

/**
 * POST /simulations/:id/share 본문. scope 가 없으면 새 링크는 NEW_SHARE_SCOPE(비공개),
 * 이미 있는 링크는 범위를 바꾸지 않는다 — 옛 앱이 다시 공유를 눌러도 소유자가 고른 모드가 바뀌지 않게.
 * scope 를 보내면 있던 링크(같은 주소)의 모드가 바로 바뀐다
 */
export const shareCreateSchema = z.object({ scope: z.enum(SHARE_SCOPES).optional() })
export type ShareCreateInput = z.infer<typeof shareCreateSchema>

/**
 * no_cost_formula 링크가 내보내는 결과. 실을 칸을 하나씩 고른다(허용 목록) — 결과 스키마에 칸이 늘어도 저절로 새지 않게.
 * 남기는 것: 제품 소개, 재료 이름, 판매가·비슷한 제품 가격대(원가가 아니다), 알레르기. 검증 안내는 웹의 공통 PRODUCT_DRAFT_COPY만 사용.
 * 빼는 것(원가·배합): batch(생산 단위 수·수율·배합비 기준), 재료의 qty·unit·sourceQty·qtyStatus·ratio·share·unitPrice·
 * priceEvidence·cost·substitutes·role·risk·id·fn, 공정 steps·plainSteps(모델 문장에 수치가 섞일 수 있어 둘 다 제외), costs 전체,
 * pricing.marginPct, estPh, baseNote, complianceSummary, caution(배합 농도 같은 규정 메모가 들어갈 수 있다).
 */
export interface ShareResultWithoutCostFormula {
  language: SimulationPublic['language']
  market: SimulationPublic['market']
  currency: SimulationPublic['currency']
  axis: SimulationPublic['axis']
  mode: SimulationPublic['mode']
  product: Pick<
    SimulationPublic['product'],
    | 'name'
    | 'tagline'
    | 'categoryId'
    | 'subtype'
    | 'format'
    | 'netWeight'
    | 'shelfLifeDays'
    | 'storage'
    | 'howToUse'
  >
  ingredients: { name: string }[]
  pricing: Pick<SimulationPublic['pricing'], 'suggestedRetail' | 'marketRange'>
  allergens: string[]
  allergenRecheck?: boolean
}

function withoutCostFormula(result: SimulationPublic): ShareResultWithoutCostFormula {
  return {
    language: result.language,
    market: result.market,
    currency: result.currency,
    axis: result.axis,
    mode: result.mode,
    product: {
      name: result.product.name,
      tagline: result.product.tagline,
      categoryId: result.product.categoryId,
      subtype: result.product.subtype,
      format: result.product.format,
      netWeight: { amount: result.product.netWeight.amount, unit: result.product.netWeight.unit },
      shelfLifeDays: result.product.shelfLifeDays,
      storage: result.product.storage,
      howToUse: result.product.howToUse,
    },
    ingredients: result.ingredients.map((i) => ({ name: i.name })),
    pricing: {
      suggestedRetail: result.pricing.suggestedRetail,
      marketRange: { min: result.pricing.marketRange.min, max: result.pricing.marketRange.max },
    },
    allergens: [...result.allergens],
    ...(result.allergenRecheck === undefined ? {} : { allergenRecheck: result.allergenRecheck }),
  }
}

/** GET /share/:token 의 scope 와 result 짝. 옛 API 는 scope 칸이 없다 — 받는 쪽은 없으면 full 로 본다 */
export type SharedResult =
  | { scope: 'full'; result: SimulationPublic }
  | { scope: 'no_cost_formula'; result: ShareResultWithoutCostFormula }

export function sharedResultForScope(result: SimulationPublic, scope: ShareScope): SharedResult {
  // 인증 준비도는 공개하지 않는다 (2026-11-01 대표 결정: 1단계 숨김). 소비자에게 인증 표시처럼 읽힐 수 있다
  const { certReadiness: _private, ...shared } = result
  return scope === 'no_cost_formula'
    ? { scope, result: withoutCostFormula(shared) }
    : { scope, result: shared }
}
